import { forwardRef, ReactNode } from 'react';
import classNames from 'classnames';

/**
 * How much room a card gives its contents.
 *
 * This was two booleans — `halfPadding` and `noPadding` — which is a
 * three-value scale encoded so that two of its values can be asked for at
 * once. Both are still honoured so no caller has to change, but a scale says
 * what it means and leaves room for a fourth step.
 */
export type CardPadding = 'none' | 'tight' | 'snug' | 'roomy';

const PADDING: Record<CardPadding, string> = {
  none: 'p-0',
  tight: 'p-3',
  snug: 'p-5',
  roomy: 'p-10',
};

export interface CardProps {
  className?: string;
  children: ReactNode;
  padding?: CardPadding;
  /**
   * Shown above the body, with a divider. A card whose heading is part of its
   * body has to re-solve the spacing every time, and they drift apart.
   */
  title?: ReactNode;
  /** Sits opposite the title — a menu, a filter, a link. Needs a title. */
  actions?: ReactNode;
  /** Padding applied to the header and body separately. */
  headerClassName?: string;
  bodyClassName?: string;
  /** @deprecated pass `padding="snug"`. */
  halfPadding?: boolean;
  /** @deprecated pass `padding="none"`. */
  noPadding?: boolean;
}

const resolvePadding = ({
  padding,
  halfPadding,
  noPadding,
}: Pick<CardProps, 'padding' | 'halfPadding' | 'noPadding'>): CardPadding => {
  if (padding) return padding;
  // Checked in this order because both booleans could be passed, and "no
  // padding" is the more specific request.
  if (noPadding) return 'none';
  if (halfPadding) return 'snug';
  return 'roomy';
};

export const Card = forwardRef(
  (
    {
      className,
      children,
      padding,
      title,
      actions,
      headerClassName,
      bodyClassName,
      halfPadding,
      noPadding,
    }: CardProps,
    ref
  ) => {
    const step = resolvePadding({ padding, halfPadding, noPadding });
    const hasHeader = title !== undefined && title !== null;

    return (
      <div
        ref={ref as any}
        className={classNames(
          'shadow bg-white rounded-lg overflow-auto',
          // With a header the padding belongs to each part, so the divider
          // can reach the card's edges instead of floating inside a box.
          !hasHeader && PADDING[step],
          className
        )}
      >
        {hasHeader && (
          <div
            className={classNames(
              'flex items-start justify-between gap-3 border-b border-light-alternate',
              PADDING[step === 'roomy' ? 'snug' : step],
              'pb-3',
              headerClassName
            )}
          >
            <div className="min-w-0">{title}</div>
            {actions ? <div className="shrink-0">{actions}</div> : null}
          </div>
        )}
        {hasHeader ? (
          <div className={classNames(PADDING[step], bodyClassName)}>
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    );
  }
);

export default Card;
